import { useCallback, useEffect, useState } from "react";
import { authFetch, authFetchJson } from "@/lib/api";
import { formatBytes, formatDateTime } from "@/lib/format";
import { Pagination } from "@/components/Pagination";
import { RecordingAudioPlayer } from "@/components/RecordingAudioPlayer";
import { REVEAL_REASON_MAX, REVEAL_REASON_MIN, isValidRevealReason } from "./revealReason";
import { translateArchiveDisplay } from "./translateArchiveDisplay";

type AdminRecording = {
  id: string;
  user_id: string;
  user_full_name: string;
  user_email: string;
  organization_id: string | null;
  organization_name: string;
  department_id: string | null;
  department_name: string;
  display_name: string;
  recorded_at: string;
  expires_at: string;
  duration_seconds: number | null;
  size_bytes: number | null;
  mime_type: string | null;
  source: string | null;
  status: string | null;
};

type AdminRecordingDetail = AdminRecording & {
  s3_key: string | null;
  patient_ref: string;
  deleted_at: string | null;
  created_at: string | null;
};

type RecordingsResponse = {
  recordings: AdminRecording[];
  total: number;
};

type RecordingResult = {
  transcript: { text: string; updated_at: string | null } | null;
  ambient: {
    text: string;
    structured_json: Record<string, unknown>;
    updated_at: string | null;
  } | null;
};

const SOURCE_OPTIONS = ["live", "ambient"] as const;
const STATUS_OPTIONS = [
  "uploading",
  "uploaded",
  "transcribed",
  "failed",
  "deleted",
] as const;

function fmtDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function DeleteConfirmModal({
  recording,
  onCancel,
  onConfirm,
  busy,
}: {
  recording: AdminRecording;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-bold text-rose-700">Opname verwijderen</h2>
        <p className="mb-2 text-sm text-[var(--cs-navy)]">
          Are you sure? This action cannot be undone.
        </p>
        <div className="mb-4 rounded-lg bg-[var(--cs-ivory)] p-3 text-xs text-[var(--cs-navy)]">
          <p className="font-mono">{recording.display_name}</p>
          {recording.user_full_name || recording.user_email ? (
            <p>Gebruiker: {recording.user_full_name || recording.user_email}</p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? "Verwijderen…" : "Verwijderen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RevealTranscriptModal({
  onCancel,
  onConfirm,
  busy,
  error,
}: {
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  busy: boolean;
  error: string;
}) {
  const [reason, setReason] = useState("");
  const valid = isValidRevealReason(reason);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-bold text-rose-700">Transcriptie tonen</h2>
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <p className="mb-1 font-medium">
            Deze opname en transcriptie kunnen persoonsgegevens en medische gegevens bevatten.
          </p>
          <p>
            Bekijk, beluister of bewerk ze alleen met schriftelijke toestemming van de klant.
            Elke inzage, elke keer afspelen en elke wijziging wordt met je naam en het tijdstip
            in de auditlog vastgelegd, samen met je reden.
          </p>
        </div>
        <label
          htmlFor="reveal-reason"
          className="mb-1 block text-xs font-medium text-[var(--cs-navy)]"
        >
          Reden (verplicht)
        </label>
        <textarea
          id="reveal-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={REVEAL_REASON_MAX}
          placeholder="bv. Schriftelijke vraag van de klant via ticket #123"
          className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
        />
        <p className="mt-1 text-[11px] text-[var(--cs-ink-muted)]">
          Minstens {REVEAL_REASON_MIN} tekens.
        </p>
        {error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || !valid}
            className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {busy ? "Laden…" : "Tonen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordingDetailModal({
  recordingId,
  canDelete,
  onClose,
  onSaved,
  onRequestDelete,
}: {
  recordingId: string;
  canDelete: boolean;
  onClose: () => void;
  onSaved: () => void;
  onRequestDelete: (rec: AdminRecording) => void;
}) {
  const [detail, setDetail] = useState<AdminRecordingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [recordedAt, setRecordedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [duration, setDuration] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [patientRef, setPatientRef] = useState("");

  // Transcript, audio and result editing stay hidden until the admin gives a
  // reason; the backend audits every reveal and only serves audio / accepts
  // edits after a recent one. Nothing is kept across modal opens.
  const [result, setResult] = useState<RecordingResult | null>(null);
  const [transcriptText, setTranscriptText] = useState("");
  const [ambientText, setAmbientText] = useState("");
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealError, setRevealError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setResult(null);
      setError("");
      try {
        const resp = await authFetchJson<AdminRecordingDetail>(
          `/api/admin/recordings/${recordingId}`,
        );
        if (cancelled) return;
        setDetail(resp);
        setRecordedAt(toDateTimeLocal(resp.recorded_at));
        setExpiresAt(toDateTimeLocal(resp.expires_at));
        setDuration(resp.duration_seconds == null ? "" : String(resp.duration_seconds));
        setSource(resp.source ?? "");
        setStatus(resp.status ?? "");
        setPatientRef(resp.patient_ref ?? "");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Laden mislukt.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recordingId]);

  const handleReveal = async (reason: string) => {
    setRevealBusy(true);
    setRevealError("");
    try {
      const resp = await authFetchJson<RecordingResult>(
        `/api/admin/recordings/${recordingId}/reveal-transcript`,
        { method: "POST", body: JSON.stringify({ reason }) },
      );
      setResult(resp);
      setTranscriptText(resp.transcript?.text ?? "");
      setAmbientText(resp.ambient?.text ?? "");
      setRevealOpen(false);
    } catch (err) {
      setRevealError(err instanceof Error ? err.message : "Transcriptie laden mislukt.");
    } finally {
      setRevealBusy(false);
    }
  };

  const origTranscriptText = result?.transcript?.text ?? "";
  const origAmbientText = result?.ambient?.text ?? "";
  const ambientJson = result?.ambient?.structured_json ?? null;
  // Ambient-only recordings show just the summary; everything else shows the transcript.
  const showTranscript = result != null && (result.transcript != null || result.ambient == null);
  const translateArchive = translateArchiveDisplay(origTranscriptText);
  const playable = detail?.status === "uploaded" || detail?.status === "transcribed";

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    setError("");
    try {
      const payload: Record<string, unknown> = {};

      const recordedIso = fromDateTimeLocal(recordedAt);
      if (recordedIso && recordedIso !== detail.recorded_at) {
        payload.recorded_at = recordedIso;
      }
      const expiresIso = fromDateTimeLocal(expiresAt);
      if (expiresIso && expiresIso !== detail.expires_at) {
        payload.expires_at = expiresIso;
      }
      const trimmedDur = duration.trim();
      const newDur = trimmedDur === "" ? null : Number(trimmedDur);
      if (newDur !== detail.duration_seconds) {
        if (newDur != null && (Number.isNaN(newDur) || newDur < 0)) {
          throw new Error("Duur moet een positief getal zijn.");
        }
        payload.duration_seconds = newDur;
      }
      if (source && source !== detail.source) payload.source = source;
      if (status && status !== detail.status) payload.status = status;
      if (patientRef.trim() !== (detail.patient_ref ?? "")) {
        payload.patient_ref = patientRef.trim();
      }

      const resultPayload: Record<string, unknown> = {};
      if (showTranscript && !translateArchive && transcriptText !== origTranscriptText) {
        resultPayload.transcript_text = transcriptText;
      }
      if (result?.ambient && ambientText !== origAmbientText) {
        resultPayload.ambient_text = ambientText;
      }

      const metaChanged = Object.keys(payload).length > 0;
      const resultChanged = Object.keys(resultPayload).length > 0;
      if (!metaChanged && !resultChanged) {
        onClose();
        return;
      }

      if (metaChanged) {
        await authFetchJson<AdminRecordingDetail>(
          `/api/admin/recordings/${recordingId}`,
          { method: "PATCH", body: JSON.stringify(payload) },
        );
      }
      if (resultChanged) {
        await authFetchJson<{ ok: boolean }>(
          `/api/admin/recordings/${recordingId}/result`,
          { method: "PUT", body: JSON.stringify(resultPayload) },
        );
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[var(--cs-navy)]">Opname bewerken</h2>
          <button type="button" onClick={onClose} className="text-[var(--cs-ink-subtle)] hover:text-[var(--cs-navy)]">
            ✕
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
        ) : !detail ? (
          <p className="text-sm text-red-600">{error || "Opname niet gevonden."}</p>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg bg-[var(--cs-ivory)] p-3 text-xs text-[var(--cs-navy)] sm:grid-cols-2">
              <p>
                <span className="text-[var(--cs-ink-muted)]">ID:</span>{" "}
                <span className="font-mono">{detail.id.slice(0, 8)}…</span>
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">Bestandsnaam:</span>{" "}
                <span className="font-mono">{detail.display_name}</span>
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">Gebruiker:</span>{" "}
                {detail.user_full_name || detail.user_email || "—"}
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">E-mail:</span> {detail.user_email || "—"}
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">Ziekenhuis:</span>{" "}
                {detail.organization_name || "—"}
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">Afdeling:</span>{" "}
                {detail.department_name || "—"}
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">Grootte:</span>{" "}
                {formatBytes(detail.size_bytes)}
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">MIME:</span> {detail.mime_type || "—"}
              </p>
              <p className="sm:col-span-2 break-all">
                <span className="text-[var(--cs-ink-muted)]">S3-sleutel:</span>{" "}
                <span className="font-mono">{detail.s3_key || "—"}</span>
              </p>
              <p>
                <span className="text-[var(--cs-ink-muted)]">Aangemaakt:</span>{" "}
                {formatDateTime(detail.created_at)}
              </p>
            </div>

            {result == null ? (
              <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3">
                <p className="mb-2 text-xs text-rose-800">
                  De opname en transcriptie zijn verborgen. Ze kunnen persoonsgegevens bevatten
                  en mogen alleen met schriftelijke toestemming van de klant bekeken, beluisterd
                  of bewerkt worden.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setRevealError("");
                    setRevealOpen(true);
                  }}
                  className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
                >
                  Transcriptie tonen
                </button>
              </div>
            ) : (
              <div className="mb-4 space-y-3">
                {detail.status === "uploading" ? (
                  <div className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white p-3">
                    <p className="mb-2 text-xs font-medium text-[var(--cs-navy)]">Afspelen</p>
                    <p className="text-xs text-[var(--cs-ink-muted)]">Opname wordt nog geüpload…</p>
                  </div>
                ) : detail.status === "failed" ? (
                  <div className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white p-3">
                    <p className="mb-2 text-xs font-medium text-[var(--cs-navy)]">Afspelen</p>
                    <p className="text-xs text-rose-600">
                      Upload mislukt — alleen de eigenaar kan opnieuw uploaden.
                    </p>
                  </div>
                ) : playable ? (
                  <RecordingAudioPlayer
                    recordingId={recordingId}
                    downloadUrl={`/api/admin/recordings/${recordingId}/download`}
                    mimeType={detail.mime_type}
                    displayName={detail.display_name}
                    sizeBytes={detail.size_bytes}
                  />
                ) : null}
                {showTranscript && translateArchive ? (
                  <div>
                    <p className="mb-1 text-xs font-medium text-[var(--cs-navy)]">
                      Vertaalresultaat (alleen-lezen)
                    </p>
                    <div className="whitespace-pre-wrap rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-2 text-sm leading-6 text-[var(--cs-navy)]">
                      {translateArchive.displayText || "Geen vertaalresultaat opgeslagen."}
                    </div>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-[var(--cs-ink-muted)]">
                        Ruwe vertaalgegevens (alleen-lezen)
                      </summary>
                      <pre className="mt-1 max-h-60 overflow-auto rounded-lg bg-[var(--cs-ivory)] p-2 text-[11px] text-[var(--cs-navy)]">
                        {translateArchive.rawText}
                      </pre>
                    </details>
                  </div>
                ) : showTranscript ? (
                  <div>
                    <label
                      htmlFor="admin-transcript"
                      className="mb-1 block text-xs font-medium text-[var(--cs-navy)]"
                    >
                      Transcript
                    </label>
                    <textarea
                      id="admin-transcript"
                      value={transcriptText}
                      onChange={(e) => setTranscriptText(e.target.value)}
                      rows={8}
                      placeholder="Geen transcript opgeslagen — typ hier om er een toe te voegen."
                      className="w-full whitespace-pre-wrap rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-2 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
                    />
                  </div>
                ) : null}
                {result.ambient ? (
                  <div>
                    <label
                      htmlFor="admin-ambient"
                      className="mb-1 block text-xs font-medium text-[var(--cs-navy)]"
                    >
                      Samenvatting (Sjabloon)
                    </label>
                    <textarea
                      id="admin-ambient"
                      value={ambientText}
                      onChange={(e) => setAmbientText(e.target.value)}
                      rows={8}
                      className="w-full whitespace-pre-wrap rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-2 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
                    />
                    {ambientJson && Object.keys(ambientJson).length > 0 ? (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-[var(--cs-ink-muted)]">
                          Structuur (alleen-lezen)
                        </summary>
                        <pre className="mt-1 max-h-60 overflow-auto rounded-lg bg-[var(--cs-ivory)] p-2 text-[11px] text-[var(--cs-navy)]">
                          {JSON.stringify(ambientJson, null, 2)}
                        </pre>
                      </details>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}

            {error ? (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                {error}
              </div>
            ) : null}

            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
                    Opgenomen op
                  </label>
                  <input
                    type="datetime-local"
                    value={recordedAt}
                    onChange={(e) => setRecordedAt(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
                    Verloopt op
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
                    Duur (seconden)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Bron</label>
                  <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
                  >
                    {SOURCE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
                    Patiëntnummer
                  </label>
                  <input
                    type="text"
                    maxLength={64}
                    value={patientRef}
                    onChange={(e) => setPatientRef(e.target.value)}
                    className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
              {canDelete ? (
                <button
                  type="button"
                  onClick={() => onRequestDelete(detail)}
                  className="rounded-lg border border-red-200 bg-white px-4 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Verwijderen
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  {saving ? "Opslaan…" : "Opslaan"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {revealOpen ? (
        <RevealTranscriptModal
          busy={revealBusy}
          error={revealError}
          onCancel={() => setRevealOpen(false)}
          onConfirm={(reason) => void handleReveal(reason)}
        />
      ) : null}
    </div>
  );
}

type OrgOption = { id: string; name: string };
type DeptOption = { id: string; name: string; organization_id: string };

export function AdminRecordingsTab({
  mode = "global",
  canDelete = true,
  showDeptPicker = false,
}: {
  mode?: "global" | "department";
  canDelete?: boolean;
  showDeptPicker?: boolean;
} = {}) {
  const [recordings, setRecordings] = useState<AdminRecording[]>([]);
  const [pickerOrgId, setPickerOrgId] = useState("");
  const [pickerDeptId, setPickerDeptId] = useState("");
  const [pickerOrgs, setPickerOrgs] = useState<OrgOption[]>([]);
  const [pickerDepts, setPickerDepts] = useState<DeptOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [qUser, setQUser] = useState("");
  const [qHospital, setQHospital] = useState("");
  const [qDepartment, setQDepartment] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AdminRecording | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (!showDeptPicker) return;
    void Promise.all([
      authFetchJson<{ organizations: OrgOption[] }>("/api/admin/organizations").catch(() => ({ organizations: [] })),
      authFetchJson<{ departments: DeptOption[] }>("/api/admin/departments").catch(() => ({ departments: [] })),
    ]).then(([orgs, depts]) => {
      setPickerOrgs(orgs.organizations);
      setPickerDepts(depts.departments);
    });
  }, [showDeptPicker]);

  const pickerFilteredDepts = pickerOrgId
    ? pickerDepts.filter((d) => d.organization_id === pickerOrgId)
    : pickerDepts;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (mode === "global") {
        if (qUser.trim()) params.set("q_user", qUser.trim());
        if (qHospital.trim()) params.set("q_hospital", qHospital.trim());
        if (qDepartment.trim()) params.set("q_department", qDepartment.trim());
      } else if (showDeptPicker) {
        // Superuser viewing the department tab: structured org/department filter.
        if (qUser.trim()) params.set("q_user", qUser.trim());
        if (pickerOrgId) params.set("organization_id", pickerOrgId);
        if (pickerDeptId) params.set("department_id", pickerDeptId);
      }
      // department mode without picker: no params — the backend forces the
      // caller's own department.
      const qs = params.toString();
      const url = qs ? `/api/admin/recordings?${qs}` : "/api/admin/recordings";
      const resp = await authFetchJson<RecordingsResponse>(url);
      setRecordings(resp.recordings ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [mode, showDeptPicker, qUser, qHospital, qDepartment, pickerOrgId, pickerDeptId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void load();
    }, 250);
    return () => window.clearTimeout(handle);
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [qUser, qHospital, qDepartment, pickerOrgId, pickerDeptId, pageSize]);

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setError("");
    try {
      await authFetch(`/api/admin/recordings/${deleting.id}`, { method: "DELETE" });
      setDeleting(null);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verwijderen mislukt.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(recordings.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedRecordings = recordings.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  return (
    <div className="space-y-4">
      {mode === "global" ? (
        <div className="grid grid-cols-1 gap-2 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-[var(--cs-navy)]" htmlFor="rec-q-user">Naam / e-mail</label>
            <input id="rec-q-user" type="text" value={qUser} onChange={(e) => setQUser(e.target.value)} placeholder="bv. Jan Janssens" className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--cs-navy)]" htmlFor="rec-q-hospital">Ziekenhuis</label>
            <input id="rec-q-hospital" type="text" value={qHospital} onChange={(e) => setQHospital(e.target.value)} placeholder="bv. AZ Gent" className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--cs-navy)]" htmlFor="rec-q-department">Afdeling</label>
            <input id="rec-q-department" type="text" value={qDepartment} onChange={(e) => setQDepartment(e.target.value)} placeholder="bv. Cardiologie" className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]" />
          </div>
        </div>
      ) : showDeptPicker ? (
        <div className="grid grid-cols-1 gap-2 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-[var(--cs-navy)]" htmlFor="rec-pick-org">Ziekenhuis</label>
            <select id="rec-pick-org" value={pickerOrgId} onChange={(e) => { setPickerOrgId(e.target.value); setPickerDeptId(""); }} className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm">
              <option value="">— Alle —</option>
              {pickerOrgs.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--cs-navy)]" htmlFor="rec-pick-dept">Afdeling</label>
            <select id="rec-pick-dept" value={pickerDeptId} onChange={(e) => setPickerDeptId(e.target.value)} className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm">
              <option value="">— Alle —</option>
              {pickerFilteredDepts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--cs-navy)]" htmlFor="rec-q-user2">Naam / e-mail</label>
            <input id="rec-q-user2" type="text" value={qUser} onChange={(e) => setQUser(e.target.value)} placeholder="bv. Jan Janssens" className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]" />
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--cs-ink-muted)]">Opnames van jouw afdeling.</p>
      )}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : recordings.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Geen opnames gevonden.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
            <table className="w-full text-sm">
              <thead className="bg-[var(--cs-ivory)]">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                  <th className="px-4 py-2">Opname</th>
                  <th className="px-4 py-2">Gebruiker</th>
                  <th className="px-4 py-2">Ziekenhuis</th>
                  <th className="px-4 py-2">Afdeling</th>
                  <th className="px-4 py-2">Bron</th>
                  <th className="px-4 py-2">Duur</th>
                  <th className="px-4 py-2">Grootte</th>
                  <th className="px-4 py-2">Opgenomen</th>
                  <th className="px-4 py-2">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {pagedRecordings.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setEditingId(r.id)}
                    className="cursor-pointer text-xs hover:bg-[var(--cs-ivory)]"
                  >
                    <td className="px-4 py-2 font-mono text-[11px] text-[var(--cs-navy)]">
                      <div className="flex items-center gap-2">
                        <span>{r.display_name}</span>
                        {r.status === "uploading" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[9px] font-medium text-sky-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500" />
                            Uploaden…
                          </span>
                        ) : r.status === "failed" ? (
                          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[9px] font-medium text-rose-700">
                            Mislukt
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <p className="font-medium text-[var(--cs-navy)]">
                        {r.user_full_name || r.user_email || "—"}
                      </p>
                      {r.user_full_name && r.user_email ? (
                        <p className="text-[10px] text-[var(--cs-ink-muted)]">{r.user_email}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-[var(--cs-navy)]">{r.organization_name || "—"}</td>
                    <td className="px-4 py-2 text-[var(--cs-navy)]">{r.department_name || "—"}</td>
                    <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">{r.source ?? "—"}</td>
                    <td className="px-4 py-2 tabular-nums text-[var(--cs-navy)]">
                      {fmtDuration(r.duration_seconds)}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-[var(--cs-navy)]">
                      {formatBytes(r.size_bytes)}
                    </td>
                    <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
                      {formatDateTime(r.recorded_at, { timeZone: "Europe/Brussels" })}
                    </td>
                    <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setEditingId(r.id)}
                          className="rounded border border-[rgba(10,52,96,0.10)] bg-white px-2 py-0.5 text-[10px] text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
                        >
                          Bewerk
                        </button>
                        {canDelete ? (
                          <button
                            type="button"
                            onClick={() => setDeleting(r)}
                            className="rounded border border-red-200 bg-white px-2 py-0.5 text-[10px] text-red-700 hover:bg-red-50"
                          >
                            Verwijder
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            total={recordings.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {editingId ? (
        <RecordingDetailModal
          recordingId={editingId}
          canDelete={canDelete}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            void load();
          }}
          onRequestDelete={(rec) => setDeleting(rec)}
        />
      ) : null}

      {deleting ? (
        <DeleteConfirmModal
          recording={deleting}
          busy={deleteBusy}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void handleConfirmDelete()}
        />
      ) : null}
    </div>
  );
}
